import { useState, type SyntheticEvent } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { redirectIfAuthenticated } from '@/lib/auth-guards';
import { authClient } from '@/lib/auth-client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { PasswordField } from '@/components/ui/Field';
import { AuthLayout } from '@/features/auth/AuthLayout';

const MIN_PASSWORD_LENGTH = 10;

export const Route = createFileRoute('/reset-password')({
  beforeLoad: () => redirectIfAuthenticated(),
  // Better Auth redirects here as `/reset-password?token=…` (or `?error=INVALID_TOKEN`).
  validateSearch: (search: Record<string, unknown>): { token?: string; error?: string } => ({
    ...(typeof search['token'] === 'string' ? { token: search['token'] } : {}),
    ...(typeof search['error'] === 'string' ? { error: search['error'] } : {}),
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token, error: linkError } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!token || linkError) {
    return (
      <AuthLayout
        title="This link has expired"
        description="Reset links work for one hour and only once."
        footer={
          <Link to="/sign-in" className="font-bold text-primary hover:underline">
            Back to log in
          </Link>
        }
      >
        <Link
          to="/forgot-password"
          className="focus-ring inline-flex h-13 w-full items-center justify-center rounded-[12px] bg-primary px-6 text-[17px] font-bold text-white no-underline hover:bg-primary-hover"
        >
          Send a new link
        </Link>
      </AuthLayout>
    );
  }

  async function onSubmit(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!token) return;
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setPending(true);
    const { error: resetError } = await authClient.resetPassword({ newPassword: password, token });
    setPending(false);
    if (resetError) {
      setError('We could not change your password. The link may have expired. Ask for a new one.');
      return;
    }
    await navigate({ to: '/sign-in' });
  }

  return (
    <AuthLayout
      title="Choose a new password"
      description="Use at least 10 characters."
      footer={
        <Link to="/sign-in" className="font-bold text-primary hover:underline">
          Back to log in
        </Link>
      }
    >
      <form className="grid gap-4" onSubmit={(event) => void onSubmit(event)}>
        {error ? <Alert tone="danger" title={error} /> : null}

        <PasswordField
          label="New password"
          required
          autoComplete="new-password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />

        <Button type="submit" size="lg" fullWidth loading={pending}>
          Save new password
        </Button>
      </form>
    </AuthLayout>
  );
}
